// Primitivos de brush: geram planos de colisão (convexos) + geometria de render
// a partir dos MESMOS vértices — colisão e visual nunca divergem.
// Convenção: normal do plano aponta para FORA do brush; plano = { n, d } com n·x = d.
import * as THREE from 'three';
import type { Plane } from '@surfrun/shared';
import { Brush } from '../physics/trace';
import { v3 } from '../physics/vec';

export interface BuiltBrush {
  brush: Brush;
  geometry: THREE.BufferGeometry | null; // null p/ invisível
  mat: string;
}

function planeFromPoints(a: number[], b: number[], c: number[]): Plane {
  // normal = (b-a) × (c-a)
  const ab = [b[0]! - a[0]!, b[1]! - a[1]!, b[2]! - a[2]!];
  const ac = [c[0]! - a[0]!, c[1]! - a[1]!, c[2]! - a[2]!];
  let nx = ab[1]! * ac[2]! - ab[2]! * ac[1]!;
  let ny = ab[2]! * ac[0]! - ab[0]! * ac[2]!;
  let nz = ab[0]! * ac[1]! - ab[1]! * ac[0]!;
  const l = Math.hypot(nx, ny, nz);
  nx /= l; ny /= l; nz /= l;
  return { n: [nx, ny, nz], d: nx * a[0]! + ny * a[1]! + nz * a[2]! };
}

/** Escala de textura: 1 repetição a cada 128 units (padrão dev-texture). */
const TEX_WORLD_SIZE = 128;

/** Constrói geometria a partir de faces (fans de triângulos), com normais planas e
 *  UVs por projeção planar no eixo dominante do normal (estilo texturas do Hammer). */
function geomFromFaces(faces: number[][][]): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  for (const face of faces) {
    const p = planeFromPoints(face[0]!, face[1]!, face[2]!);
    const [nx, ny, nz] = [Math.abs(p.n[0]), Math.abs(p.n[1]), Math.abs(p.n[2])];
    // eixos de projeção: descarta o eixo dominante do normal
    let ua = 0, va = 2; // padrão: chão (normal y) → uv = x,z
    if (nx >= ny && nx >= nz) { ua = 2; va = 1; }       // parede x → uv = z,y
    else if (nz >= nx && nz >= ny) { ua = 0; va = 1; }  // parede z → uv = x,y
    for (let i = 1; i < face.length - 1; i++) {
      for (const vtx of [face[0]!, face[i]!, face[i + 1]!]) {
        positions.push(vtx[0]!, vtx[1]!, vtx[2]!);
        normals.push(p.n[0], p.n[1], p.n[2]);
        uvs.push(vtx[ua]! / TEX_WORLD_SIZE, vtx[va]! / TEX_WORLD_SIZE);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  return g;
}

/** Garante que toda face aponte para FORA do brush (winding manual vira detalhe). */
function orientFacesOutward(faces: number[][][]): number[][][] {
  let cx = 0, cy = 0, cz = 0, n = 0;
  for (const f of faces) for (const p of f) { cx += p[0]!; cy += p[1]!; cz += p[2]!; n++; }
  cx /= n; cy /= n; cz /= n;
  return faces.map((f) => {
    const pl = planeFromPoints(f[0]!, f[1]!, f[2]!);
    let fx = 0, fy = 0, fz = 0;
    for (const p of f) { fx += p[0]!; fy += p[1]!; fz += p[2]!; }
    fx /= f.length; fy /= f.length; fz /= f.length;
    const outward = pl.n[0] * (fx - cx) + pl.n[1] * (fy - cy) + pl.n[2] * (fz - cz);
    return outward < 0 ? [...f].reverse() : f;
  });
}

/**
 * Planos de bevel (algoritmo padrão de compiladores BSP): planos axiais da AABB do
 * brush + bevels de aresta (cross(aresta, eixo)). Sem eles, o trace por expansão de
 * planos cria "bolsos" fantasma perto de arestas/picos onde a AABB do player entra
 * e trava (allSolid). Com eles, a soma de Minkowski brush⊕caixa fica EXATA.
 */
function addBevelPlanes(faces: number[][][], planes: Plane[]): void {
  const EPS = 0.01;
  const verts: number[][] = [];
  for (const f of faces) for (const p of f) {
    if (!verts.some((v) => Math.abs(v[0]! - p[0]!) < EPS && Math.abs(v[1]! - p[1]!) < EPS && Math.abs(v[2]! - p[2]!) < EPS)) {
      verts.push(p);
    }
  }
  const hasPlane = (n: number[], d: number): boolean =>
    planes.some((pl) => pl.n[0] * n[0]! + pl.n[1] * n[1]! + pl.n[2] * n[2]! > 0.999 && Math.abs(pl.d - d) < 0.1);
  const support = (n: number[]): number => Math.max(...verts.map((v) => n[0]! * v[0]! + n[1]! * v[1]! + n[2]! * v[2]!));
  const tryAdd = (n: number[]): void => {
    const d = support(n);
    if (!hasPlane(n, d)) planes.push({ n: [n[0]!, n[1]!, n[2]!], d });
  };

  // 1) planos axiais (AABB)
  for (let a = 0; a < 3; a++) {
    for (const s of [1, -1]) {
      const n = [0, 0, 0]; n[a] = s;
      tryAdd(n);
    }
  }
  // 2) bevels de aresta: para cada aresta e cada eixo, n = cross(aresta, eixo);
  //    válido se os DOIS extremos da aresta estão no suporte do plano
  for (const f of faces) {
    for (let i = 0; i < f.length; i++) {
      const a = f[i]!, b = f[(i + 1) % f.length]!;
      const e = [b[0]! - a[0]!, b[1]! - a[1]!, b[2]! - a[2]!];
      for (let ax = 0; ax < 3; ax++) {
        const axv = [0, 0, 0]; axv[ax] = 1;
        let nx = e[1]! * axv[2]! - e[2]! * axv[1]!;
        let ny = e[2]! * axv[0]! - e[0]! * axv[2]!;
        let nz = e[0]! * axv[1]! - e[1]! * axv[0]!;
        const l = Math.hypot(nx, ny, nz);
        if (l < 1e-6) continue;
        nx /= l; ny /= l; nz /= l;
        for (const s of [1, -1]) {
          const n = [nx * s, ny * s, nz * s];
          const d = support(n);
          const da = n[0]! * a[0]! + n[1]! * a[1]! + n[2]! * a[2]!;
          const db = n[0]! * b[0]! + n[1]! * b[1]! + n[2]! * b[2]!;
          if (Math.abs(da - d) < EPS && Math.abs(db - d) < EPS && !hasPlane(n, d)) {
            planes.push({ n: [n[0]!, n[1]!, n[2]!], d });
          }
        }
      }
    }
  }
}

/** Rotaciona faces em yaw (graus) ao redor de um pivô — colisão e render derivam
 *  dos vértices, então isso habilita curvas/segmentos em qualquer ângulo. */
export function rotateFacesY(faces: number[][][], deg: number, pivot: [number, number, number]): number[][][] {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r), s = Math.sin(r);
  const [px, , pz] = pivot;
  return faces.map((f) => f.map((v) => {
    const x = v[0]! - px, z = v[2]! - pz;
    return [px + x * c + z * s, v[1]!, pz - x * s + z * c];
  }));
}

function brushFromFaces(rawFaces: number[][][], mat: string, surf = false, yaw = 0, pivot: [number, number, number] = [0, 0, 0]): BuiltBrush {
  if (yaw !== 0) rawFaces = rotateFacesY(rawFaces, yaw, pivot);
  const faces = orientFacesOutward(rawFaces);
  const planes = faces.map((f) => planeFromPoints(f[0]!, f[1]!, f[2]!));
  addBevelPlanes(faces, planes);
  let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (const f of faces) for (const p of f) {
    minX = Math.min(minX, p[0]!); maxX = Math.max(maxX, p[0]!);
    minY = Math.min(minY, p[1]!); maxY = Math.max(maxY, p[1]!);
    minZ = Math.min(minZ, p[2]!); maxZ = Math.max(maxZ, p[2]!);
  }
  return {
    brush: { planes, surf, mat, min: v3(minX, minY, minZ), max: v3(maxX, maxY, maxZ) },
    geometry: geomFromFaces(faces),
    mat,
  };
}

/** Caixa (yaw opcional ao redor do pivô). */
export function box(
  min: [number, number, number], max: [number, number, number], mat = 'floor',
  yaw = 0, pivot?: [number, number, number],
): BuiltBrush {
  const [x0, y0, z0] = min, [x1, y1, z1] = max;
  const faces: number[][][] = [
    [[x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0]], // topo (+y)
    [[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]], // base (-y)
    [[x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1]], // +x
    [[x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]], // -x
    [[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], // +z
    [[x0, y0, z0], [x0, y1, z0], [x1, y1, z0], [x1, y0, z0]], // -z
  ];
  return brushFromFaces(faces, mat, false, yaw, pivot ?? [(x0 + x1) / 2, 0, (z0 + z1) / 2]);
}

/**
 * Rampa de surf clássica: prisma triangular ao longo de Z, pico em cima no centro.
 * center = [x, yBase, zCenter]; width = base total; height = altura do pico; length = ao longo de Z.
 * Surfa-se nas duas faces inclinadas. (normal.y da face = (w/2)/hyp — validador exige < 0.7)
 */
export function surfRamp(
  center: [number, number, number], width: number, height: number, length: number, mat = 'ramp',
  yaw = 0,
): BuiltBrush {
  const [cx, y0, cz] = center;
  const hw = width / 2, hl = length / 2;
  const y1 = y0 + height;
  const A = [cx - hw, y0, cz - hl], B = [cx + hw, y0, cz - hl], T1 = [cx, y1, cz - hl];
  const C = [cx - hw, y0, cz + hl], D = [cx + hw, y0, cz + hl], T2 = [cx, y1, cz + hl];
  const faces: number[][][] = [
    [A, B, D, C],           // base (-y)
    [A, T1, B],             // tampa -z
    [C, D, T2],             // tampa +z
    [A, C, T2, T1],         // face inclinada esquerda (-x)
    [B, T1, T2, D],         // face inclinada direita (+x)
  ];
  return brushFromFaces(faces, mat, true, yaw, [cx, y0, cz]);
}

/**
 * Cunha (rampa de chão de um lado só), subindo ao longo de +Z ou -Z / ±X.
 * min/max definem a caixa; `high` diz qual lado fica alto ('z+', 'z-', 'x+', 'x-').
 */
export function wedge(
  min: [number, number, number], max: [number, number, number],
  high: 'z+' | 'z-' | 'x+' | 'x-', mat = 'floor', surf = false,
): BuiltBrush {
  const [x0, y0, z0] = min, [x1, y1, z1] = max;
  // 4 vértices da base + 2 do topo (no lado alto)
  let faces: number[][][];
  if (high === 'z+') {
    const A = [x0, y0, z0], B = [x1, y0, z0], C = [x1, y0, z1], D = [x0, y0, z1];
    const E = [x0, y1, z1], F = [x1, y1, z1];
    faces = [[A, B, C, D], [A, E, F, B].reverse(), [D, C, F, E], [A, D, E], [B, F, C]];
  } else if (high === 'z-') {
    const A = [x0, y0, z0], B = [x1, y0, z0], C = [x1, y0, z1], D = [x0, y0, z1];
    const E = [x0, y1, z0], F = [x1, y1, z0];
    faces = [[A, B, C, D], [D, C, F, E].reverse(), [A, E, F, B], [A, D, E].reverse(), [B, F, C].reverse()];
  } else if (high === 'x+') {
    const A = [x0, y0, z0], B = [x1, y0, z0], C = [x1, y0, z1], D = [x0, y0, z1];
    const E = [x1, y1, z0], F = [x1, y1, z1];
    faces = [[A, B, C, D], [A, D, F, E].reverse(), [B, E, F, C], [A, E, B].reverse(), [D, C, F].reverse()];
  } else {
    const A = [x0, y0, z0], B = [x1, y0, z0], C = [x1, y0, z1], D = [x0, y0, z1];
    const E = [x0, y1, z0], F = [x0, y1, z1];
    faces = [[A, B, C, D], [B, E, F, C].reverse(), [A, D, F, E], [A, E, B], [D, C, F]];
  }
  return brushFromFaces(faces, mat, surf);
}
